import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { buildDayTimeline, TIMELINE_HOURS } from "@/lib/calendar/mobile-agenda";
import type { CalendarEvent } from "@/components/calendar/views/CalendarEventTypes";
import { getEventPetsLabel, hexToRgba } from "@/components/calendar/views/calendarEventUtils";

type Props = {
  date: Date;
  grouped: Map<string, CalendarEvent[]>;
  onAppointmentClick: (id: string) => void;
};

const HOUR_HEIGHT = 60;

type MobileTimelineEventButtonProps = {
  block: ReturnType<typeof buildDayTimeline>[number];
  onAppointmentClick: (id: string) => void;
};

function MobileTimelineEventButton({
  block,
  onAppointmentClick,
}: MobileTimelineEventButtonProps) {
  const event = block.event;
  const start = format(new Date(event.start), "HH:mm", { locale: ptBR });
  const end = format(new Date(event.end), "HH:mm", { locale: ptBR });

  return (
    <button
      type="button"
      onClick={() => onAppointmentClick(event.id)}
      className="flex h-full w-full flex-col overflow-hidden rounded-xl px-2 py-1 text-left shadow-sm transition-colors active:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      style={{
        backgroundColor: `color-mix(in srgb, ${block.color} 8%, hsl(var(--card)))`,
        borderLeft: `2px solid ${block.color}`,
        color: "hsl(var(--foreground))",
      }}
    >
      <span className="block truncate text-[10px] font-semibold tabular-nums leading-tight text-foreground">
        {start} – {end}
      </span>
      <span className="mt-0.5 block truncate text-[12px] font-bold leading-tight text-foreground">
        {getEventPetsLabel(event)}
      </span>
    </button>
  );
}

export function MobileDayView({ date, grouped, onAppointmentClick }: Props) {
  const { t } = useTranslation("calendar");
  const blocks = buildDayTimeline(date, grouped);

  const earlyBlocks = blocks.filter((b) => b.isEarly);
  const lateBlocks = blocks.filter((b) => b.isLate);
  const normalBlocks = blocks.filter((b) => !b.isEarly && !b.isLate);

  return (
    <div className="rounded-3xl border border-border/60 bg-card px-1 pb-3 pt-2 shadow-sm">
      {/* Early events — rendered above the 08:00 line */}
      {earlyBlocks.length > 0 && (
        <div className="mb-1 flex flex-col gap-1 px-1.5 pb-1.5">
          {earlyBlocks.map((block) => {
            return (
              <MobileTimelineEventButton
                key={block.event.id}
                block={block}
                onAppointmentClick={onAppointmentClick}
              />
            );
          })}
        </div>
      )}

      <div className="relative">
        {TIMELINE_HOURS.map((label) => (
          <div key={label} className="flex" style={{ height: `${HOUR_HEIGHT}px` }}>
            <div className="-translate-y-1.5 w-[46px] shrink-0 pr-2 text-right text-[11px] font-semibold tabular-nums text-muted-foreground">
              {label}
            </div>
            <div className="flex-1 border-t border-border/60" />
          </div>
        ))}
        <div className="absolute inset-y-0 left-[46px] right-1.5">
          {(() => {
            // Assign columns to overlapping events
            type BlockWithCol = (typeof normalBlocks)[number] & { col: number; totalCols: number };
            const placed: BlockWithCol[] = [];
            for (const block of normalBlocks) {
              const blockTop = block.topPx;
              const blockBottom = block.topPx + block.heightPx;
              const overlapping = placed.filter(
                (p) => p.topPx < blockBottom && p.topPx + p.heightPx > blockTop
              );
              const usedCols = new Set(overlapping.map((p) => p.col));
              let col = 0;
              while (usedCols.has(col)) col++;
              placed.push({ ...block, col, totalCols: 1 });
            }
            // Second pass: expand totalCols so all overlapping blocks share the same count
            for (const block of placed) {
              const blockTop = block.topPx;
              const blockBottom = block.topPx + block.heightPx;
              const overlapping = placed.filter(
                (p) => p.topPx < blockBottom && p.topPx + p.heightPx > blockTop
              );
              const maxCol = Math.max(...overlapping.map((p) => p.col));
              const total = maxCol + 1;
              for (const p of overlapping) p.totalCols = Math.max(p.totalCols, total);
            }
            const GAP = 2;
            return placed.map((block) => {
              const end = format(new Date(block.event.end), "HH:mm", { locale: ptBR });
              const start = format(new Date(block.event.start), "HH:mm", { locale: ptBR });
              const widthPct = 100 / block.totalCols;
              const leftPct = widthPct * block.col;
              return (
                <button
                  key={block.event.id}
                  type="button"
                  onClick={() => onAppointmentClick(block.event.id)}
                  className="absolute flex flex-col gap-px overflow-hidden rounded-xl px-2 py-1.5 text-left"
                  style={{
                    top: `${block.topPx}px`,
                    height: `${block.heightPx}px`,
                    left: `calc(${leftPct}% + ${block.col > 0 ? GAP : 0}px)`,
                    width: `calc(${widthPct}% - ${block.col > 0 ? GAP : 0}px)`,
                    backgroundColor: hexToRgba(block.color, 0.16) ?? "transparent",
                    borderLeft: `3px solid ${block.color}`,
                    color: block.color,
                  }}
                >
                  <span className="text-[11px] font-semibold tabular-nums opacity-90">{start} – {end}</span>
                  <span className="truncate text-[13px] font-bold leading-tight">{block.event.title}</span>
                  <span className="truncate text-[11px] opacity-80">
                    {block.event.clinic_appointment_type?.name ?? t("appointmentLabel")}
                  </span>
                </button>
              );
            });
          })()}
        </div>
      </div>
      {/* Late events — rendered below the last timeline hour */}
      {lateBlocks.length > 0 && (
        <div className="mt-1 flex flex-col gap-1 px-1.5 pt-1.5">
          {lateBlocks.map((block) => {
            return (
              <MobileTimelineEventButton
                key={block.event.id}
                block={block}
                onAppointmentClick={onAppointmentClick}
              />
            );
          })}
        </div>
      )}
      {blocks.length === 0 ? (
        <div className="px-4 pb-1 pt-3.5 text-center text-[13px] text-muted-foreground">{t("mobile.emptyDay")}</div>
      ) : null}
    </div>
  );
}
